import toast, { Toaster } from "react-hot-toast";
import { Link } from "react-router";
import { useAppSelector } from "../hooks/reduxToolkit";
import { present } from "../../lib/util/present";
import { useState, useEffect, useRef } from "react";
import { Tooltip } from "./tooltip";
import { useSentry } from "../providers/sentry";
import { throttle } from "async-toolbox";
import { useSubscriptionAccess } from "../hooks/useSubscriptionAccess";
import { ResubscribeLink } from "./subscriptionBanner";

import './alertBanner.scss'

export default function AlertBanner() {
  const isAccountDeletionRequested = useAppSelector((s) => present(s.membership?.profile?.account_deletion_requested_at))
  const isMaintenanceMode = useIsMaintenanceMode();
  const { isReadOnly } = useSubscriptionAccess();
  const membership = useAppSelector(state => state.membership);
  const organization = useAppSelector(state => state.membership.organization);

  // Only check subscription status if user is authenticated (has a membership loaded)
  const isAuthenticated = present(membership?.membershipId);

  // isReadOnly is the server's verdict (see isSubscriptionReadOnly). This used to
  // re-check the organization here and suppress the banner whenever the org looked
  // subscribed, which is how an expired trial could leave a member with no banner at
  // all. Whether an org covers this member is already part of that verdict, so the
  // only thing the org state decides now is the wording.
  const isSubscriptionExpired = isAuthenticated && isReadOnly;

  let banner: JSX.Element | null = null;
  if (isAccountDeletionRequested) {
    banner = <Link to={'/deleteAccount'} className="a-block">
      <div className="bg-danger text-white text-center">
        Account Deletion Pending
      </div>
    </Link>
  } else if (isMaintenanceMode) {
    banner =
    <Tooltip tooltip="Some features may be temporarily unavailable while we perform maintenance."
      htmlElement="div"
      className="bg-warning text-white text-center">
        <span>The system is under maintenance.</span>
    </Tooltip>
  } else if (isSubscriptionExpired) {
    const hasLapsedOrgSubscription = organization && organization.subscription_end_date &&
      new Date(organization.subscription_end_date) <= new Date();

    banner = <div className="bg-warning text-white text-center">
      <strong>{hasLapsedOrgSubscription ? "Your organization's subscription has expired." : "Your subscription has expired."}</strong>{' '}
      <ResubscribeLink />
    </div>
  }

  return <div className="alert-banner">
      {banner}

      <Toaster />
    </div>
}


function useIsMaintenanceMode() {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const Sentry = useSentry();

  useEffect(() => {

    function doCheck() {
      checkMaintenanceMode()
        .then((isActive) => {
          setIsMaintenanceMode((wasActive) => {
            if (wasActive && !isActive) {
              toast.success('Maintenance has ended.');
            }
            return isActive;
          });
          if (isActive) {
            // check every second
            timeout.current = setTimeout(doCheck, 1_000);
          } else {
            // check every minute
            timeout.current = setTimeout(doCheck, 60_000);
          }
        })
        .catch((error) => {
          Sentry.captureException(error);
          // Don't let a transient failure (e.g. a one-off 5xx from the
          // maintenance-mode endpoint) permanently stop the poll — retry
          // on the same cadence as a normal "not in maintenance" check.
          timeout.current = setTimeout(doCheck, 60_000);
        });
    }

    // Check once on load, then check every second only if we're in maintenance mode
    doCheck();

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    }
  }, []);

  return isMaintenanceMode;
}

const checkMaintenanceMode = throttle(async () => {
  // Skip in dev
  if (process.env.NODE_ENV === 'development') {
    return false;
  }
   
  const res = await fetch('/maintenance-mode-active', {
    cache: 'no-store'
  });
  
  if (res.ok) {
    return true;
  } else {
    if (res.status === 404) {
      return false;
    } else {
      throw new Error('Failed to check maintenance mode');
    }
  }
}, 1000);